import React, { useState, useEffect, useCallback, useRef } from "react";
import { FiSearch } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import myAxios from "../AxiosUrl";
import styles from "./SearchDesktop.module.css";

const SearchDesktopOptimized = ({ uid, onSearchResults, onLoading }) => {
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef(null);
  const abortController = useRef(null);

  // Debounced search function
  const performSearch = useCallback(async (searchQuery) => {
    // Cancel previous request if exists
    if (abortController.current) {
      abortController.current.abort();
    }

    if (!searchQuery || searchQuery.trim() === '') {
      // If search is empty, clear results and show all products
      onSearchResults(null);
      setIsSearching(false);
      if (onLoading) onLoading(false);
      return;
    }

    // Create new abort controller for this request
    abortController.current = new AbortController();

    try {
      setIsSearching(true);
      if (onLoading) onLoading(true);

      const response = await myAxios.get('search-products', {
        params: {
          uid,
          searchQuery: searchQuery.trim(),
          page: 1,
          limit: 50 // Load more results for better UX
        },
        signal: abortController.current.signal
      });

      if (response.data.success) {
        onSearchResults(response.data.data);
      } else {
        onSearchResults([]);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Search error:', error);
        onSearchResults([]);
      }
    } finally {
      setIsSearching(false);
      if (onLoading) onLoading(false);
    }
  }, [uid, onSearchResults, onLoading]);

  // Handle input change with debouncing
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for 300ms debounce
    debounceTimer.current = setTimeout(() => {
      performSearch(searchValue);
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Immediately trigger search on submit
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    performSearch(search);
  };

  const clearSearch = () => {
    setSearch("");
    onSearchResults(null);
    setIsSearching(false);
    if (onLoading) onLoading(false);

    // Cancel any pending requests
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (abortController.current) {
      abortController.current.abort();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return (
    <form className={styles["search-desktop-container"]} onSubmit={handleSubmit}>
      <div className={styles["search-desktop-input"]}>
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={handleSearch}
        />
        {isSearching ? (
          <div className={styles["search-loading-spinner"]}>
            <div className={styles["spinner"]}></div>
          </div>
        ) : search !== "" ? (
          <AiOutlineClose
            className={styles["search-desktop-close-icon"]}
            onClick={clearSearch}
          />
        ) : (
          <FiSearch className={styles["search-desktop-search-icon"]} />
        )}
      </div>
    </form>
  );
};

export default SearchDesktopOptimized;
